import * as React from 'react';
import { useCallback, useState } from 'react';

import { type CourseFrameContextType, NavSingleItemDisplayV2, useCourseFrameContext } from '@coursera-web/course-frame';

import { get } from 'js/lib/user';

import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import NavPeerReviewItemDisplay from 'bundles/learner-progress/components/item/nav/private/NavPeerReviewItemDisplay';
import NavSingleItemDisplay from 'bundles/learner-progress/components/item/nav/private/NavSingleItemDisplay';
import type { Item as ItemType } from 'bundles/learner-progress/types/Item';
import { getIsAssignmentPartForSplitPeer } from 'bundles/learner-progress/utils/Item';
import withComputedItem from 'bundles/learner-progress/utils/withComputedItem';
import { useNaptime } from 'bundles/naptimejs';
import {
  GuidedCourseSessionProgressesV1NaptimeResource as GuidedCourseSessionProgressesV1,
  type GuidedCourseSessionProgressesV1 as GuidedCourseSessionProgressesV1Type,
} from 'bundles/naptimejs/resources/__generated__/GuidedCourseSessionProgressesV1';
import { shouldEnabledFreemiumLoadTesting } from 'bundles/ondemand/epicFeatureFlags';

type PropsFromCaller = {
  itemMetadata?: ItemMetadata;
  highlighted?: boolean;
  trackingName?: string;
  openInNewWindow?: boolean;
  iconSize?: number;
};

type PropsFromWithComputedItem = {
  computedItem: ItemType;
};

type PropsFromNavItemNaptimeWrapper = {
  triggerGuidedCourseProgressesFetch?: () => void;
} & Pick<CourseFrameContextType, 'isCourseFrameEnabled'>;

// Props for the original NavItem class component
type NavItemInnerProps = PropsFromCaller & PropsFromWithComputedItem & PropsFromNavItemNaptimeWrapper;

// Individual item shown in the navigation on item pages
export class NavItemInner extends React.Component<NavItemInnerProps> {
  isFreemiumLoadTestingEnabled: boolean;

  constructor(props: NavItemInnerProps) {
    super(props);
    this.isFreemiumLoadTestingEnabled = shouldEnabledFreemiumLoadTesting();
  }

  componentDidUpdate(prevProps: NavItemInnerProps) {
    const { computedItem, triggerGuidedCourseProgressesFetch } = this.props;
    const prevComputedItem = prevProps.computedItem;

    if (
      computedItem &&
      prevComputedItem &&
      prevComputedItem.isCompleted !== computedItem.isCompleted &&
      this.isFreemiumLoadTestingEnabled &&
      triggerGuidedCourseProgressesFetch
    ) {
      triggerGuidedCourseProgressesFetch();
    }
  }

  render() {
    const { computedItem, highlighted, isCourseFrameEnabled, trackingName, openInNewWindow, iconSize } = this.props;

    if (getIsAssignmentPartForSplitPeer(computedItem)) {
      return (
        <NavPeerReviewItemDisplay
          computedItem={computedItem}
          highlighted={highlighted}
          trackingName={trackingName}
          openInNewWindow={openInNewWindow}
          iconSize={iconSize}
        />
      );
    }

    return (
      <div>
        {isCourseFrameEnabled ? (
          <NavSingleItemDisplayV2 computedItem={computedItem} highlighted={highlighted} />
        ) : (
          <NavSingleItemDisplay
            computedItem={computedItem}
            highlighted={highlighted}
            trackingName={trackingName}
            openInNewWindow={openInNewWindow}
            iconSize={iconSize}
          />
        )}
      </div>
    );
  }
}

// Props for the wrapper component - same as original NavItem's PropsFromCaller + computedItem from HOC
type PropsToNavItemWrapper = PropsFromCaller & PropsFromWithComputedItem;

const NavItemNaptimeWrapper = (props: PropsToNavItemWrapper) => {
  const { computedItem, ...restProps } = props;
  const { isCourseFrameEnabled } = useCourseFrameContext();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const userId = get()?.id;
  const courseId = computedItem?.courseId;

  const { naptime } = useNaptime<{
    guidedCourseProgresses?: GuidedCourseSessionProgressesV1Type[];
  }>(() => {
    if (refreshTrigger === 0 || !userId || !courseId) {
      return {};
    }

    return {
      guidedCourseProgresses: GuidedCourseSessionProgressesV1.multiGet([`${userId}~${courseId}`], {
        fields: ['weeks'],
      }),
    };
  }, [userId, courseId, refreshTrigger]);

  const triggerGuidedCourseProgressesFetch = useCallback<() => void>(() => {
    if (!userId || !courseId) {
      return;
    }

    setRefreshTrigger((prev) => prev + 1);

    if (naptime) {
      naptime.refreshData({ resources: [GuidedCourseSessionProgressesV1.RESOURCE_NAME] }).catch((error: Error) => {
        console.error(
          'sfx NavItemNaptimeWrapper: naptime.refreshData call failed for',
          GuidedCourseSessionProgressesV1.RESOURCE_NAME,
          error
        );
      });
    }
  }, [userId, courseId, naptime]);

  return (
    <NavItemInner
      {...restProps}
      computedItem={computedItem}
      triggerGuidedCourseProgressesFetch={triggerGuidedCourseProgressesFetch}
      isCourseFrameEnabled={isCourseFrameEnabled}
    />
  );
};

export default withComputedItem(NavItemNaptimeWrapper);
